import { useCallback, useEffect, useState } from 'react';
import { api } from '@halo/web-lib';
import { BellRing, RefreshCw, CheckCircle2 } from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

interface AlertPolicy {
  name: string;
  displayName: string;
  enabled: boolean;
  conditionCount: number;
  notificationChannels: string[];
  creationTime: string | null;
}

interface AlertsResponse {
  policies: AlertPolicy[];
}

/* ─── Helpers ─────────────────────────────────── */

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function EnabledBadge({ enabled }: { enabled: boolean }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        enabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      disabled
    </span>
  );
}

function ChannelsCell({ channels }: { channels: string[] }) {
  if (channels.length === 0) {
    return <span className="text-gray-400 dark:text-gray-600 italic">none</span>;
  }
  const joined = channels.join(', ');
  const truncated = joined.length > 60 ? `${joined.slice(0, 57)}…` : joined;
  return (
    <span
      className="font-mono text-[11px] text-gray-700 dark:text-gray-200"
      title={joined}
    >
      {truncated}
    </span>
  );
}

/* ─── Main Component ──────────────────────────── */

export function AlertsPage() {
  const [policies, setPolicies] = useState<AlertPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get<AlertsResponse>('/api/v1/ingestion/admin/monitoring/alerts');
      if (res.success && res.data) {
        const sorted = [...res.data.policies].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );
        setPolicies(sorted);
      } else {
        setError('Failed to load alert policies.');
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setError('Failed to load alert policies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BellRing className="w-5 h-5" />
            Alert Policies
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Cloud Monitoring alert policies configured for ingestion services.
          </p>
        </div>
        <button
          onClick={fetchAlerts}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 rounded-xl p-4 text-sm">
          {error}
        </div>
      ) : policies.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <CheckCircle2 className="w-10 h-10 text-gray-400 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">No alert policies found.</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">Configure alert policies in Cloud Monitoring.</div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Display name</th>
                <th className="text-left px-4 py-2 font-medium">Enabled</th>
                <th className="text-right px-4 py-2 font-medium">Conditions</th>
                <th className="text-left px-4 py-2 font-medium">Notification channels</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr
                  key={p.name}
                  className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-950/50"
                >
                  <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{p.displayName}</td>
                  <td className="px-4 py-2.5">
                    <EnabledBadge enabled={p.enabled} />
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-gray-700 dark:text-gray-200">
                    {p.conditionCount}
                  </td>
                  <td className="px-4 py-2.5">
                    <ChannelsCell channels={p.notificationChannels} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400" title={p.creationTime ?? ''}>
                    {formatRelative(p.creationTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
